import { useState } from 'react'
import { createSpace, setSpaceShared } from '@core/expenses'
import type { SpaceKind } from '@core/categories'
import type { Space } from '@core/session'
import { Button, Card, ErrorNote, Field } from '@design/components'

/**
 * A space you can add after the fact, and choose to open.
 *
 * The switch is the interesting part, and it is off by default. Everything in
 * this app has assumed one direction — family members send, the administrator
 * sees — and this is the first thing that lets a space go the other way. That
 * is a decision about your own privacy, so it is made per space, in words,
 * with the consequence written next to it rather than implied by a label.
 */
export function NewSpace({
  spaces,
  onCreated,
}: {
  spaces: Space[]
  onCreated: () => void
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<SpaceKind>('household')
  const [shared, setShared] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full mb-4 min-h-[2.75rem] rounded-xl border border-line text-sm font-medium text-muted"
      >
        Ruimte toevoegen
      </button>
    )
  }

  return (
    <Card className="mb-4">
      <p className="font-medium mb-3">Nieuwe ruimte</p>

      <Field
        label="Naam"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Lissabon"
        autoFocus
      />

      <p className="text-sm text-muted mb-1.5">Wat voor ruimte?</p>
      <div className="flex gap-2 mb-4">
        {(['household', 'business'] as SpaceKind[]).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`flex-1 min-h-[3rem] rounded-xl border text-sm font-medium ${
              k === kind ? 'border-ink bg-ink/5' : 'border-line text-muted'
            }`}
          >
            {k === 'business' ? 'Zaak' : 'Privé'}
          </button>
        ))}
      </div>

      <label className="flex items-start justify-between gap-3 py-2 mb-2">
        <span className="text-sm">
          De rest van het gezin mag meekijken
          <span className="block text-xs text-muted mt-0.5">
            Ze zien de bedragen en de bonnetjes in deze ruimte — en alleen in
            deze. Ze kunnen niets wijzigen. Je kan dit later omzetten.
          </span>
        </span>
        <input
          type="checkbox"
          checked={shared}
          onChange={(e) => setShared(e.target.checked)}
          className="w-6 h-6 shrink-0 mt-0.5"
        />
      </label>

      <ErrorNote>{error}</ErrorNote>

      <div className="flex gap-2">
        <Button tone="quiet" onClick={() => setOpen(false)}>
          Annuleren
        </Button>
        <Button
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              await createSpace({ name, kind, membersSeeMoney: shared })
              setOpen(false)
              setName('')
              onCreated()
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Aanmaken lukte niet.')
              setBusy(false)
            }
          }}
        >
          {busy ? 'Bezig…' : 'Maken'}
        </Button>
      </div>

      {spaces.length >= 4 && (
        <p className="text-xs text-muted mt-3">
          Je hebt er al {spaces.length}. Elke ruimte is een eigen filter, een
          eigen totaal en een eigen lijst — meer dan een handvol maakt het
          overzicht juist kleiner.
        </p>
      )}
    </Card>
  )
}

export { setSpaceShared }
